import React, { useState } from 'react';
import { useTranslation } from '@/i18n';
import BaseDialog from './BaseDialog';
import { TransitionConfig } from '../../pages/VideoEditPage';

// 转场类型枚举 - 只包含后端支持的基础类型
export type TransitionBaseType = 'fade' | 'dissolve' | 'wipe' | 'slide' | 'push' | 'circlecrop' | 'rectcrop';

// 转场方向枚举
export type TransitionDirection = 'left' | 'right' | 'up' | 'down';

// 对话框属性接口
export interface TransitionSettingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sceneId: string;
  sceneNumber: number;
  initialTransition?: TransitionConfig;
  onSaveTransition: (sceneId: string, transition: TransitionConfig) => void;
  sceneList: any[]; // Added sceneList prop for preview
}

const TransitionSettingDialog: React.FC<TransitionSettingDialogProps> = ({
  isOpen,
  onClose,
  sceneId,
  sceneNumber,
  initialTransition,
  onSaveTransition,
  sceneList
}) => {
  const { t } = useTranslation();

  // Get current scene and next scene from sceneList
  const currentSceneIndex = sceneList.findIndex(scene => scene.id === sceneId);
  const currentScene = sceneList[currentSceneIndex];
  const nextScene = sceneList[currentSceneIndex + 1]; // Transition is to the next scene

  // 转场效果选项 - 基础类型
  const transitionTypeOptions = [
    { value: 'fade', label: t('transition.fade') },
    { value: 'dissolve', label: t('transition.dissolve') },
    { value: 'wipe', label: t('transition.wipe') },
    { value: 'slide', label: t('transition.slide') },
    { value: 'push', label: t('transition.push') },
    { value: 'circlecrop', label: t('transition.circlecrop') },
    { value: 'rectcrop', label: t('transition.rectcrop') }
  ];

  // 转场方向选项
  const directionOptions = [
    { value: 'left', label: t('transition.left') },
    { value: 'right', label: t('transition.right') },
    { value: 'up', label: t('transition.up') },
    { value: 'down', label: t('transition.down') }
  ];

  // 缓动函数选项 - 后端支持的格式
  const easingOptions = [
    { value: 'linear', label: t('transition.linear') },
    { value: 'in', label: t('transition.in') },
    { value: 'out', label: t('transition.out') },
    { value: 'inout', label: t('transition.inOut') },
    { value: 'easein', label: 'easein' },
    { value: 'easeout', label: 'easeout' }
  ];

  // 状态管理
  // 解析初始转场配置
  const initialType = (() => {
    if (initialTransition?.type) {
      // 如果是旧格式（如slide_left），提取基础类型
      if (initialTransition.type.includes('_')) {
        return initialTransition.type.split('_')[0] as TransitionBaseType;
      }
      return initialTransition.type as TransitionBaseType;
    }
    return 'fade';
  })();

  const initialDirection = (() => {
    if (initialTransition?.direction) {
      return initialTransition.direction as TransitionDirection;
    }
    // 如果是旧格式（如slide_left），提取方向
    if (initialTransition?.type && initialTransition.type.includes('_')) {
      return initialTransition.type.split('_')[1] as TransitionDirection;
    }
    return 'right';
  })();

  // 初始颜色
  const initialColor = initialTransition?.color || '#000000';

  const initialDuration = (() => {
    if (initialTransition?.duration) {
      // 如果是秒，转换为毫秒
      if (initialTransition.duration < 10) {
        return initialTransition.duration * 1000;
      }
      return initialTransition.duration;
    }
    return 500;
  })();

  const initialEasing = initialTransition?.easing || 'inout';

  const [transitionType, setTransitionType] = useState<TransitionBaseType>(initialType);
  const [direction, setDirection] = useState<TransitionDirection>(initialDirection);
  const [duration, setDuration] = useState<number>(initialDuration);
  const [easing, setEasing] = useState<string>(initialEasing);
  const [color, setColor] = useState<string>(initialColor);

  // 当 initialTransition 变化时更新状态
  // 解决所有场景打开对话框显示相同UI的问题
  React.useEffect(() => {
    if (!initialTransition) return;

    // 计算新的初始值
    const newType = (() => {
      if (initialTransition?.type) {
        if (initialTransition.type.includes('_')) {
          return initialTransition.type.split('_')[0] as TransitionBaseType;
        }
        return initialTransition.type as TransitionBaseType;
      }
      return 'fade';
    })();

    const newDirection = (() => {
      if (initialTransition?.direction) {
        return initialTransition.direction as TransitionDirection;
      }
      if (initialTransition?.type && initialTransition.type.includes('_')) {
        return initialTransition.type.split('_')[1] as TransitionDirection;
      }
      return 'right';
    })();

    const newColor = initialTransition?.color || '#000000';

    const newDuration = (() => {
      if (initialTransition?.duration) {
        if (initialTransition.duration < 10) {
          return initialTransition.duration * 1000;
        }
        return initialTransition.duration;
      }
      return 500;
    })();

    const newEasing = initialTransition?.easing || 'inout';

    // 更新所有状态
    setTransitionType(newType);
    setDirection(newDirection);
    setDuration(newDuration);
    setEasing(newEasing);
    setColor(newColor);
  }, [initialTransition]);

  // 保存转场配置
  const handleSave = () => {
    // 将毫秒转换为秒
    const durationInSeconds = duration / 1000;

    const transitionConfig: TransitionConfig = {
      type: transitionType,
      duration: parseFloat(durationInSeconds.toFixed(2)),
      easing
    };

    // 只有在需要方向的转场类型时才添加direction字段
    const typesNeedDirection = ['slide', 'push', 'wipe'];
    if (typesNeedDirection.includes(transitionType)) {
      transitionConfig.direction = direction;
    }

    // 只有wipe类型需要color字段
    if (transitionType === 'wipe') {
      transitionConfig.color = color;
    }

    onSaveTransition(sceneId, transitionConfig);
    onClose();
  };

  // 重置转场配置
  const handleReset = () => {
    setTransitionType('fade');
    setDirection('right');
    setDuration(500);
    setEasing('inout');
    setColor('#000000');
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={`#${sceneNumber} - ${t('videoEdit.transitionSetting')}`}
      width={700}
      height={600}
    >
      <div className="space-y-6 p-4">
        {/* 转场类型选择 */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">{t('videoEdit.transitionType')}</label>
          <select
            value={transitionType}
            onChange={(e) => setTransitionType(e.target.value as TransitionBaseType)}
            className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {transitionTypeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* 转场方向选择（条件显示） */}
        {(transitionType === 'slide' || transitionType === 'push' || transitionType === 'wipe') && (
          <div>
            <label className="block text-sm font-medium text-white mb-2">{t('videoEdit.transitionDirection')}</label>
            <div className="flex gap-2">
              {directionOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDirection(option.value as TransitionDirection)}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${direction === option.value ? 'bg-blue-500/50 text-white' : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 转场颜色选择（仅适用于wipe类型） */}
        {transitionType === 'wipe' && (
          <div>
            <label className="block text-sm font-medium text-white mb-2">{t('videoEdit.transitionColor')}</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-24 h-10 rounded cursor-pointer"
              />
              <span className="text-sm text-gray-400">{color}</span>
            </div>
          </div>
        )}

        {/* 转场时长设置 */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-white">{t('videoEdit.transitionDuration')}</label>
            <span className="text-sm text-gray-400">{duration}ms</span>
          </div>
          <input
            type="range"
            min="100"
            max="2000"
            step="50"
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full h-2 bg-gray-700/50 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between mt-1 text-xs text-gray-400">
            <span>100ms</span>
            <span>2000ms</span>
          </div>
        </div>

        {/* 缓动函数选择 */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">{t('videoEdit.transitionEasing')}</label>
          <select
            value={easing}
            onChange={(e) => setEasing(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {easingOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* 预览区域 */}
        <div className="p-4 bg-gray-700/30 rounded-lg">
          <h4 className="text-sm font-medium text-white mb-2">{t('videoEdit.transitionPreview')}</h4>

          {/* Preview container */}
          {nextScene ? (
            <div
            className="w-full h-32 bg-gray-800 rounded relative overflow-hidden"
            ref={(el) => {
              if (el) {
                // Trigger reflow to restart animation
                el.offsetWidth;
                // Add class to start animation
                el.classList.add('animate-transition');

                // Remove class after animation ends to allow restarting
                setTimeout(() => el.classList.remove('animate-transition'), duration);
              }
            }}
            key={`${transitionType}-${direction}-${duration}-${easing}-${color}`}
          >
            {/* Current scene preview */}
            <div
              className="absolute inset-0 flex items-center justify-center transition-all"
              style={{
                // Base styles for all transitions
                transitionDuration: `${duration}ms`,
                transitionTimingFunction: easing || 'linear',

                // Fade/dissolve transition
                opacity: transitionType === 'fade' || transitionType === 'dissolve' ? 0.3 : 1,

                // Slide/push transitions - current scene goes out
                transform:
                  (transitionType === 'slide' || transitionType === 'push') ?
                    direction === 'left' ? 'translateX(20%)' : // Current scene moves left
                    direction === 'right' ? 'translateX(-20%)' : // Current scene moves right
                    direction === 'up' ? 'translateY(20%)' : // Current scene moves up
                    'translateY(-20%)' : // Current scene moves down
                  'translateX(0)',

                // Wipe transition
                clipPath: transitionType === 'wipe' ?
                  direction === 'left' ? 'polygon(0% 0%, 80% 0%, 80% 100%, 0% 100%)' :
                  direction === 'right' ? 'polygon(20% 0%, 100% 0%, 100% 100%, 20% 100%)' :
                  direction === 'up' ? 'polygon(0% 0%, 100% 0%, 100% 80%, 0% 80%)' :
                  'polygon(0% 20%, 100% 20%, 100% 100%, 0% 100%)' :
                  'none'
              }}
            >
              {currentScene?.images?.[0] ? (
                <img
                  src={currentScene.images[0]}
                  alt="Current scene"
                  className="max-w-full max-h-full object-contain"
                />
              ) : currentScene?.videoUrl ? (
                <video
                  src={currentScene.videoUrl}
                  className="max-w-full max-h-full object-contain"
                  poster="https://via.placeholder.com/400x300?text=Video+Preview"
                />
              ) : (
                <span className="text-gray-400">No Media</span>
              )}
            </div>

            {/* Next scene preview */}
            <div
              className="absolute inset-0 flex items-center justify-center transition-all"
              style={{
                // Base styles for all transitions
                transitionDuration: `${duration}ms`,
                transitionTimingFunction: easing || 'linear',

                // Fade/dissolve transition
                opacity: transitionType === 'fade' || transitionType === 'dissolve' ? 1 : 0.7,

                // Slide/push transitions - next scene comes in
                transform:
                  (transitionType === 'slide' || transitionType === 'push') ?
                    direction === 'left' ? 'translateX(-80%)' : // Next scene comes from right to left
                    direction === 'right' ? 'translateX(80%)' : // Next scene comes from left to right
                    direction === 'up' ? 'translateY(-80%)' : // Next scene comes from bottom to top
                    'translateY(80%)' : // Next scene comes from top to bottom
                  'translateX(0)',

                // Wipe transition
                clipPath: transitionType === 'wipe' ?
                  direction === 'left' ? 'polygon(80% 0%, 100% 0%, 100% 100%, 80% 100%)' :
                  direction === 'right' ? 'polygon(0% 0%, 20% 0%, 20% 100%, 0% 100%)' :
                  direction === 'up' ? 'polygon(0% 80%, 100% 80%, 100% 100%, 0% 100%)' :
                  'polygon(0% 0%, 100% 0%, 100% 20%, 0% 20%)' :
                  'none'
              }}
            >
              {nextScene?.images?.[0] ? (
                <img
                  src={nextScene.images[0]}
                  alt="Next scene"
                  className="max-w-full max-h-full object-contain"
                />
              ) : nextScene?.videoUrl ? (
                <video
                  src={nextScene.videoUrl}
                  className="max-w-full max-h-full object-contain"
                  poster="https://via.placeholder.com/400x300?text=Video+Preview"
                />
              ) : (
                <span className="text-gray-400">No Media</span>
              )}
            </div>

            {/* Transition type indicator */}
            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
              {transitionType} {direction && `→ ${direction}`}
            </div>

            {/* Special transition indicators for crop types */}
            {(transitionType === 'circlecrop' || transitionType === 'rectcrop') && (
              <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm font-medium pointer-events-none">
                {transitionType === 'circlecrop' && '🔵 Circular Crop Transition'}
                {transitionType === 'rectcrop' && '⬜ Rectangular Crop Transition'}
              </div>
            )}
          </div>
        ) : (
          // No next scene to transition to (last scene)
          <div className="w-full h-32 bg-gray-800 rounded flex items-center justify-center">
            <span className="text-gray-400">No next scene to transition to</span>
          </div>
        )}
      </div>

        {/* 底部按钮 */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-700/50">
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-700/50 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {t('common.reset')}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700/50 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default TransitionSettingDialog;